/**
 * ForecastUpsideToggle — the per-viewer Upside % control (05.3 D-03).
 *
 * quick-261001-nym — it now lives INSIDE the Product ARR Forecast card and the
 * Managed Services ARR Forecast card (one knob each), to the right of the
 * figures and one size smaller than the dollar amounts, not on the summary
 * strip (John, 2026-10-01); v39 shrank it with the cards it sits in. On a closed quarter it is grayed and disabled —
 * there is nothing left to forecast.
 */
import { clampUpsidePct } from '../../lib/forecast-weighting.js';

interface ForecastUpsideToggleProps {
  /** The live value, owned by the page. */
  value: number;
  /** Reports a CLAMPED value; the page stores exactly what it receives. */
  onChange: (next: number) => void;
  /** Which Best Case deals this knob scales — the accessible name. */
  appliesTo?: string | undefined;
  disabled?: boolean | undefined;
  testId?: string | undefined;
}

export function ForecastUpsideToggle({
  value,
  onChange,
  appliesTo = 'Best Case deals',
  disabled = false,
  testId = 'forecast-upside-toggle',
}: ForecastUpsideToggleProps): React.JSX.Element {
  return (
    <label
      className={`flex flex-col items-end gap-0.5 text-[0.66rem] ${disabled ? 'text-ink-400' : 'text-ink-500'}`}
      data-testid={`${testId}-wrap`}
    >
      <span className="font-medium uppercase tracking-wider">Upside %</span>
      <input
        type="number"
        min={0}
        max={100}
        step={1}
        value={value}
        disabled={disabled}
        data-testid={testId}
        aria-label={`Upside percent for ${appliesTo}`}
        title={disabled ? 'Closed quarter — nothing left to forecast' : `Applied to ${appliesTo}`}
        className={`w-16 rounded-md border px-2 py-0.5 text-right text-base font-semibold tabular-nums focus:outline-none ${
          disabled
            ? 'cursor-not-allowed border-ink-200 bg-ink-100 text-ink-400'
            : 'border-ink-200 bg-white text-ink-900 focus:border-brand-500'
        }`}
        onChange={(e) => {
          onChange(clampUpsidePct(Number(e.target.value)));
        }}
      />
    </label>
  );
}
